import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface PricingTier {
  storeItemPricingTierId: number;
  storeItemId: number;
  minQuantity: number;
  maxQuantity: number | null;
  unitPrice: number;
  isActive: boolean;
  displayOrder: number;
}

export interface ShippingDetail {
  storeShippingMethodId: number;
  methodNameAr: string;
  methodNameEn: string;
  price: number;
  minDays: number;
  maxDays: number;
}

export interface CartItem {
  id: number;
  name: string;
  nameEn?: string;
  price: number;
  originalPrice?: number;
  image: string;
  quantity: number;
  minimumOrderQuantity?: number;
  isExternalOrder?: boolean;
  pricingTiers?: PricingTier[];
  shippingDetails?: ShippingDetail[];
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  calculateItemPrice: (item: CartItem) => number;
  getApplicableTier: (item: CartItem) => PricingTier | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'shopping_cart';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Load cart from localStorage on initialization
  const loadCartFromStorage = (): CartItem[] => {
    try {
      const storedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (storedCart) {
        return JSON.parse(storedCart);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    return [];
  };

  const [items, setItems] = useState<CartItem[]>(loadCartFromStorage);

  // Save cart to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [items]);

  // Calculate price for item based on quantity and pricing tiers
  const calculateItemPrice = (item: CartItem): number => {
    if (!item.pricingTiers || item.pricingTiers.length === 0) {
      return item.originalPrice || item.price;
    }

    const sortedTiers = [...item.pricingTiers].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.minQuantity - b.minQuantity
    );

    const applicableTier = sortedTiers.find(tier => {
      const minQty = tier.minQuantity;
      const maxQty = tier.maxQuantity ?? Infinity;
      return item.quantity >= minQty && item.quantity <= maxQty;
    });

    return applicableTier?.unitPrice || item.originalPrice || item.price;
  };

  // Get applicable tier for item
  const getApplicableTier = (item: CartItem): PricingTier | null => {
    if (!item.pricingTiers || item.pricingTiers.length === 0) {
      return null;
    }

    const sortedTiers = [...item.pricingTiers].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.minQuantity - b.minQuantity
    );

    const applicableTier = sortedTiers.find(tier => {
      const minQty = tier.minQuantity;
      const maxQty = tier.maxQuantity ?? Infinity;
      return item.quantity >= minQty && item.quantity <= maxQty;
    });

    return applicableTier || null;
  };

  const addToCart = (item: Omit<CartItem, 'quantity'>, quantity: number = 1) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        const updatedQuantity = existingItem.quantity + quantity;
        const updatedItem = { ...existingItem, quantity: updatedQuantity };
        // Recalculate price based on new quantity if pricing tiers exist
        if (existingItem.pricingTiers && existingItem.pricingTiers.length > 0) {
          updatedItem.price = calculateItemPrice(updatedItem);
        }
        return prevItems.map(cartItem =>
          cartItem.id === item.id ? updatedItem : cartItem
        );
      }
      const newItem = { 
        ...item, 
        quantity,
        originalPrice: item.originalPrice || item.price // Ensure originalPrice is set
      };
      // Calculate initial price based on quantity if pricing tiers exist
      if (item.pricingTiers && item.pricingTiers.length > 0) {
        newItem.price = calculateItemPrice(newItem);
      }
      return [...prevItems, newItem];
    });
  };

  const removeFromCart = (id: number) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  };

  const updateQuantity = (id: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id === id) {
          // If item has pricing tiers, update price based on new quantity
          const updatedItem = { ...item, quantity };
          if (item.pricingTiers && item.pricingTiers.length > 0) {
            updatedItem.price = calculateItemPrice(updatedItem);
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => {
      const itemPrice = calculateItemPrice(item);
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  return (
    <CartContext.Provider value={{
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getTotalItems,
      getTotalPrice,
      calculateItemPrice,
      getApplicableTier
    }}>
      {children}
    </CartContext.Provider>
  );
};
